/**
 * Intent derivation — the object every hash and every screen is computed from.
 *
 * Pure. Given PSBT bytes plus a vault context, produce a canonical description of what the
 * transaction does. The signing surface renders this; approvals commit to its hash. If the
 * intent panel and anything else ever disagree, the intent panel is authoritative.
 */
import * as btc from '@scure/btc-signer';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils.js';
import { isMine } from './descriptors.js';
/** BIP-32 hardened offset, for reading a coin type out of a derivation path. */
const HARDENED = 0x80000000;
/**
 * Purposes whose second path element really is a coin type (BIP-44/48/49/84/86).
 *
 * ⛔ **45 IS NOT ONE OF THEM AND MUST NOT BE ADDED BACK.** BIP-45 is
 * `m/45'/cosigner_index/change/index` — element 1 is which cosigner you are, not which chain
 * you are on. Reading it as a coin type turned an honest Electrum/Copay-lineage multisig PSBT
 * into `BLOCK NETWORK_MISMATCH: the inputs' derivation paths declare coin type 0 (mainnet) but
 * this vault is testnet`. A BLOCK cannot be acknowledged away, so the transaction became
 * unusable — and BIP-45 is the ORIGINAL multisig standard, which makes its users precisely this
 * product's audience.
 *
 * That is worse than the false-CRITICAL failure the scan-window rule exists to prevent: a
 * CRITICAL can at least be read and accepted by a human who understands it.
 */
const PURPOSES = new Set([44, 48, 49, 84, 86]);
/**
 * JCS RFC 8785 style: sorted keys, no insignificant whitespace.
 *
 * A `bigint` is emitted as its decimal digits inside a JSON string. JSON numbers are doubles, so
 * writing a uint64 as a bare number would round it — the very loss this type exists to avoid —
 * and `JSON.stringify` throws on bigint rather than choosing for us. A string is exact,
 * canonical (no leading zeros, no exponent form) and identical in every language that will ever
 * re-verify one of these hashes.
 */
export function jcs(v) {
    if (typeof v === 'bigint')
        return JSON.stringify(v.toString());
    /*
     * `undefined` is REFUSED, not encoded and not silently dropped.
     *
     * `JSON.stringify(undefined)` returns the JS value `undefined`, which template-concatenated
     * into the output as the literal text `undefined` — producing `{"a":1,"b":undefined}`, which
     * no JSON parser on earth accepts. The docstring promises a byte string "identical in every
     * language that will ever re-verify one of these hashes", and no other language could produce
     * that at all.
     *
     * Dropping the key instead would be worse in a different way: a hash that silently commits to
     * less than the caller believes, where `{a: 1}` and `{a: 1, b: undefined}` are the same
     * commitment. For a canonicalisation whose entire job is to make two parties agree on exactly
     * what was signed, both silent behaviours are wrong. Fail closed and make the caller decide.
     */
    if (v === undefined)
        throw new IntentError('CANONICAL_UNDEFINED', 'cannot canonicalise `undefined` — a commitment must say what it commits to. Use null, ' +
            'or omit the field deliberately.');
    if (typeof v === 'number' && !Number.isFinite(v))
        throw new IntentError('CANONICAL_NONFINITE', `cannot canonicalise ${String(v)} — JSON has no representation for it`);
    if (v === null || typeof v !== 'object')
        return JSON.stringify(v);
    if (Array.isArray(v))
        return `[${v.map(jcs).join(',')}]`;
    const o = v;
    return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${jcs(o[k])}`).join(',')}}`;
}
export const sha256hex = (s) => bytesToHex(sha256(typeof s === 'string' ? utf8ToBytes(s) : s));
/** Commits to the exact bytes served. */
export const payloadHash = (psbt) => sha256hex(psbt);
/**
 * The subset of an Intent that describes WHAT THE TRANSACTION DOES.
 *
 * ⚠️ Two rules pull in opposite directions here and BOTH are load-bearing:
 *
 *   · Anything that changes what gets paid MUST be in the hash, or a swap goes undetected.
 *   · Anything that differs between two honest participants MUST NOT be, or the room halts
 *     permanently on legitimate traffic — and `halted` is terminal by design.
 *
 * `mine`, `path` and `role` fail the second rule: they are answers produced by scanning the
 * LOCAL descriptor window, so Alice with `gapLimit 20` and Bob with `gapLimit 50` classify the
 * same change output differently and compute different hashes. Same PSBT, same vault, no
 * attacker, permanent halt. They are therefore excluded — and their absence costs nothing,
 * because ownership is re-derived independently by every client and reported through the risk
 * catalog, which is where a disagreement belongs.
 *
 * `intentHash` must be stable across every legitimate transformation a PSBT undergoes on its way
 * around a room, or the integrity check fires on honest traffic. `hasDerivation` is not stable:
 * **Bitcoin Core strips `bip32Derivation` from an input once it has signed it**, to keep the PSBT
 * small. Other wallets add or drop metadata too.
 *
 * That is a property of the CONTAINER, not of the payment. Hashing it meant a signer returning a
 * correctly-signed copy of the very transaction they were shown produced a different hash — and
 * in Org mode that is `integrity.hash_mismatch`, which **halts the room permanently**, since
 * `halted` is terminal by design (DATA-MODEL rule 4). An unrecoverable false positive on honest
 * signing.
 *
 * Everything else stays in: amounts, addresses, roles, fee, sighash and sequence are all things
 * a human approved or that change what gets paid. `hasDerivation` remains on the Intent for the
 * risk catalog, which legitimately reports `NO_DERIVATION` — it is simply not part of the
 * commitment.
 */
/**
 * Schema version of the canonical intent. **Bump this in the same commit as any change to the
 * shape below, and never otherwise.**
 *
 * Its absence was a latent permanent room halt. `canonicalIntent` used to SPREAD whatever fields
 * the `Intent` happened to carry, so adding one field to `Intent` — an ordinary thing to do —
 * silently forked the hash for every participant not yet on the new build. In Org mode a hash
 * divergence is `integrity.hash_mismatch`, which halts the room PERMANENTLY, and `halted` is
 * terminal by design. Honest traffic, two honest participants, no attacker, unrecoverable.
 *
 * An audit demonstrated the same fork three ways on one PSBT: `duplicateOutpoints: []`, the
 * field absent, and the field `undefined` produced three different hashes.
 *
 * With a version in the bytes, a mismatch becomes a legible "these two clients canonicalise
 * differently" rather than an unexplained halt — and the explicit field list below means adding
 * a field to `Intent` cannot change the hash by accident, because it will not appear here until
 * somebody puts it there.
 */
/**
 * 2 (2026-08-27) — the outpoint byte-order fix. Version 1 committed to txids that were the
 * byte-reverse of the real ones, so every v1 `intentHash` names coins that do not exist. The
 * bump is what makes that legible: a v1 and a v2 client comparing hashes get a mismatch that
 * says "these two canonicalise differently" rather than an unexplained permanent room halt.
 *
 * Safe to make now precisely because nothing has committed to a v1 hash in anger — the Org
 * surfaces that would carry approvals across clients are built and not deployed. It would not
 * be safe later, which is the reason to take it now rather than after the domain lands.
 */
export const CANONICAL_INTENT_VERSION = 2;
export function canonicalIntent(intent) {
    /*
     * EVERY FIELD IS NAMED. No spread, no rest-destructuring.
     *
     * The old version spread `intent` and deleted the fields that must not be hashed, which meant
     * the hash was defined by whatever the type happened to contain — the opposite of a
     * specification. Listing them makes the commitment a closed set: `coinTypes`, `hasDerivation`,
     * `mine`, `path` and `role` are absent because they are properties of the CONTAINER or of the
     * local scan, and two honest participants legitimately disagree about them (see the long note
     * above this function's original form — Core strips `bip32Derivation` when it signs, and Alice
     * with gapLimit 20 classifies change differently from Bob with 50).
     *
     * `?? null` on the optional arrays: an absent field and an empty array must not be two
     * different commitments, and `jcs` now refuses `undefined` outright.
     */
    return {
        v: CANONICAL_INTENT_VERSION,
        version: intent.version,
        network: intent.network,
        locktime: intent.locktime,
        rbf: intent.rbf,
        totalInSats: intent.totalInSats ?? null,
        totalOutSats: intent.totalOutSats,
        feeSats: intent.feeSats ?? null,
        vsize: intent.vsize,
        feeRate: intent.feeRate ?? null,
        duplicateOutpoints: [...(intent.duplicateOutpoints ?? [])].sort(),
        inputs: intent.inputs.map((i) => ({
            index: i.index,
            outpoint: i.outpoint,
            txid: i.txid,
            vout: i.vout,
            amountSats: i.amountSats ?? null,
            sighash: i.sighash,
            sequence: i.sequence,
        })),
        outputs: intent.outputs.map((o) => ({
            index: o.index,
            amountSats: o.amountSats,
            script: o.script,
            address: o.address ?? null,
            standard: o.standard,
        })),
    };
}
/** Commits to what this client actually rendered to this human. */
export const intentHash = (intent) => sha256hex(jcs(canonicalIntent(intent)));
/* ── vsize estimation ─────────────────────────────────────────────────────── */
/**
 * Serialized byte length of the scriptPubKey for each output type we can name.
 *
 * Used only by callers that know the TYPE but not the script. Where the script itself is on
 * hand — which is everywhere inside `deriveIntent` — pass its exact length instead: a type name
 * cannot distinguish an 80-byte OP_RETURN from a 6-byte one, and both used to be charged a flat
 * 43 vB. An OP_RETURN carrying its maximum payload is 83 bytes of script, so the estimate was
 * 62 vB light on a transaction whose fee rate a human is about to read.
 */
export const OUTPUT_SCRIPT_BYTES = {
    wpkh: 22, wsh: 34, tr: 34, pkh: 25, sh: 23, ms: 34, unknown: 34,
};
/** Bytes a CompactSize takes for `n`. Assumed to be 1 for both counts until 253 outputs. */
function varIntSize(n) {
    if (n < 0xfd)
        return 1;
    if (n <= 0xffff)
        return 3;
    if (n <= 0xffffffff)
        return 5;
    return 9;
}
/** Serialized size of one output: 8-byte amount + CompactSize script length + the script. */
const outputBytes = (scriptLen) => 8 + varIntSize(scriptLen) + scriptLen;
/**
 * Deterministic vsize for a wsh(sortedmulti) spend.
 *
 * The fee rate must be derived from a measured size, never a guessed constant — a mockup
 * assumed 1,680 vB for a 3-in/2-out 2-of-3 P2WSH when the real figure is 398, which turned an
 * intended 92 sat/vB into 389. This formula is asserted against a finalized transaction in
 * the tests.
 *
 * `outputScriptLengths` is the byte length of each output's scriptPubKey. Pass
 * `OUTPUT_SCRIPT_BYTES[type]` if a script is genuinely unavailable.
 */
/**
 * Weight units contributed by one input's witness, by what is being spent.
 *
 * `wsh-multi` is the vault's own m-of-n. The others are what a MIXED transaction actually
 * contains, and getting them wrong moves the fee rate a human reads.
 */
function witnessWU(kind, d) {
    switch (kind) {
        case 'wsh-multi': {
            // witnessScript: OP_m + n*(push33) + OP_n + CHECKMULTISIG
            const witnessScript = 3 + 34 * d.n;
            // stack: count + empty (CHECKMULTISIG off-by-one) + m signatures (~72 + length) + script push
            return 1 + 1 + d.m * 73 + 1 + witnessScript;
        }
        // count + signature (~72 + length) + pubkey (33 + length)
        case 'wpkh': return 1 + 73 + 34;
        // count + schnorr signature (64 + length). Key-path spend; a script path is larger and
        // cannot be predicted from the scriptPubKey alone.
        case 'tr': return 1 + 65;
        // Unknown witness program: assume the vault's own shape, which is the largest thing this
        // product routinely sees. Over-estimating vsize understates the fee RATE, so this is
        // reported as an assumption rather than treated as a fact — see UNKNOWN_INPUT_KIND below.
        default: return 1 + 1 + d.m * 73 + 1 + (3 + 34 * d.n);
    }
}
/**
 * Deterministic vsize estimate.
 *
 * ⚠️ IT USED TO TAKE ONLY AN INPUT COUNT, and charged every input the vault's m-of-n witness.
 * On a mixed transaction that is badly wrong in the dangerous direction: an audit built 1 vault
 * P2WSH plus 5 foreign P2WPKH inputs and got **712 vB estimated against ~529 real** — a fee rate
 * shown 26% LOW. Against a 5–20 sat/vB band that silently converts an above-band rate into
 * `PASS FEE_IN_BAND`, which is the finding a human is relying on.
 *
 * `estimateVsize(d, n, outs)` with a plain count is still accepted, because it is exactly right
 * for the all-vault case that `sim/core-parity.mjs` measures against a mined transaction (189 vs
 * 189). Pass an array of kinds when the inputs are not all the vault's.
 */
export function estimateVsize(d, inputs, outputScriptLengths) {
    const kinds = typeof inputs === 'number'
        ? Array.from({ length: inputs }, () => 'wsh-multi')
        : inputs;
    // 4 version + in-count varint + out-count varint + 4 locktime, plus segwit marker+flag (2 WU).
    const base = 4 + varIntSize(kinds.length) + varIntSize(outputScriptLengths.length) + 4 + 2 / 4;
    const ins = kinds.reduce(
    // 36 outpoint + 1 empty scriptSig + 4 sequence, then the witness at quarter weight.
    (a, kind) => a + 41 + witnessWU(kind, d) / 4, 0);
    const outs = outputScriptLengths.reduce((a, len) => a + outputBytes(len), 0);
    return Math.ceil(base + ins + outs);
}
/** Classify an input from the scriptPubKey it spends. */
export function inputKind(script) {
    if (!script)
        return 'unknown';
    // v0 P2WPKH: OP_0 PUSH20
    if (script.length === 22 && script[0] === 0x00 && script[1] === 0x14)
        return 'wpkh';
    // v0 P2WSH: OP_0 PUSH32
    if (script.length === 34 && script[0] === 0x00 && script[1] === 0x20)
        return 'wsh-multi';
    // v1 P2TR: OP_1 PUSH32
    if (script.length === 34 && script[0] === 0x51 && script[1] === 0x20)
        return 'tr';
    return 'unknown';
}
/**
 * Classify an output script.
 *
 * `@scure`'s `OutScript.decode` returns `unknown` for OP_RETURN, so a plain data output was
 * scored `CRITICAL NONSTANDARD_OUTPUT` ("may be unspendable") **and** `WARN DUST_OUTPUT` — two
 * typed acknowledgements demanded for a correct, relay-standard transaction, one of them at the
 * severity that must never cry wolf. An OP_RETURN IS unspendable; that is what it is for. This
 * package goes out of its way to support them (`allowUnknownOutputs` at every entry point, and
 * `estimateVsize` reasons about an 80-byte payload), so scoring them as an anomaly was the one
 * inconsistency left.
 *
 * `nulldata` is named separately from `unknown` because the two deserve different treatment:
 * one is a recognised script with no spender, the other is a script we could not classify.
 */
function outputType(script) {
    // OP_RETURN (0x6a) as the first opcode. Checked directly because the decoder will not name it.
    if (script.length > 0 && script[0] === 0x6a)
        return { type: 'nulldata', standard: true };
    try {
        const decoded = btc.OutScript.decode(script);
        return { type: decoded.type, standard: decoded.type !== 'unknown' };
    }
    catch {
        return { type: 'unknown', standard: false };
    }
}
function encodeAddress(script, network) {
    try {
        const net = network === 'mainnet' ? btc.NETWORK : btc.TEST_NETWORK;
        return btc.Address(net).encode(btc.OutScript.decode(script));
    }
    catch {
        return null;
    }
}
/* ── derivation ───────────────────────────────────────────────────────────── */
export class IntentError extends Error {
    code;
    constructor(code, message) {
        // Code goes in the message too: it is what shows up in logs and in test matchers.
        super(`${code}: ${message}`);
        this.code = code;
        this.name = 'IntentError';
    }
}
export function deriveIntent(psbtBytes, ctx) {
    let tx;
    try {
        tx = btc.Transaction.fromPSBT(psbtBytes, { allowUnknownOutputs: true, allowUnknownInputs: true });
    }
    catch (e) {
        throw new IntentError('MALFORMED_PSBT', `could not parse PSBT: ${e.message}`);
    }
    const inputs = [];
    const inputKinds = [];
    const duplicateOutpoints = new Set();
    const coinTypes = new Set();
    let totalIn = 0n;
    let amountsKnown = true;
    const seenOutpoints = new Set();
    for (let i = 0; i < tx.inputsLength; i++) {
        const inp = tx.getInput(i);
        /*
         * Prefer `witness_utxo`, but fall back to `non_witness_utxo`.
         *
         * Reading only `witness_utxo` meant a perfectly valid PSBT that carries the ENTIRE previous
         * transaction was treated as having no UTXO data at all — `amountSats: null`, `mine: null`,
         * and two BLOCK findings that cannot be acknowledged. The JSDoc on IntentInput even said
         * "witness_utxo / non_witness_utxo"; only one half was implemented.
         */
        let utxo = inp.witnessUtxo ?? null;
        let amountBound = false;
        if (inp.nonWitnessUtxo) {
            // `nonWitnessUtxo` decodes to a raw transaction object — `{version, inputs, outputs, ...}`
            // — not to a Transaction instance, so the earlier `prev.getOutput(i)` threw on every
            // single PSBT and the catch swallowed it. @scure/btc-signer has already checked that this
            // previous transaction hashes to the input's txid ("nonWitnessUtxo: wrong txid"), so the
            // amount read here is bound to the coin being spent, not merely asserted alongside it.
            const prev = inp.nonWitnessUtxo;
            const bound = prev.outputs?.[inp.index ?? 0] ?? null;
            /*
             * The BOUND source wins where both are present, which is the reverse of the old order.
             * Behaviourally the two agree — `validateInput` throws `witnessUtxo different from
             * nonWitnessUtxo` at parse time if they ever disagree — so this changes no amount. What
             * it changes is what we can HONESTLY SAY about the amount, and reading the weaker source
             * first meant a PSBT carrying full proof of its own input values was reported at the
             * confidence of one that carried none.
             */
            if (bound) {
                utxo = bound;
                amountBound = true;
            }
        }
        const amount = utxo ? utxo.amount : null;
        if (amount === null)
            amountsKnown = false;
        else
            totalIn += amount;
        /*
         * NO REVERSE. The comment that used to sit here — "`txid` in a PSBT is stored little-endian;
         * display order is reversed" — is true of the WIRE FORMAT and false of what we are holding.
         *
         * `@scure/btc-signer` decodes the outpoint with `txid: P.bytes(32, true)`, and that `true`
         * is the little-endian flag, so the coder has already reversed the wire bytes: `inp.txid` is
         * in display order before we touch it. Its own `validateInput` depends on that, comparing
         * `hex.encode(_i.txid)` with `Transaction.id`, and `get id()` is `sha256x2(...).reverse()`.
         * Reversing again produced, for every input the product has ever shown, a txid that is the
         * byte-reverse of the real one.
         *
         * It survived because no fixture spent a real coin. Every previous transaction was a
         * hardcoded string that is not the hash of anything, and the reverse of a made-up txid is
         * another made-up txid — so the corpus agreed with itself in both orientations. The
         * conflict check made the same mistake one level up: its only test built `spentElsewhere`
         * out of `intent.inputs[0].outpoint`, so both sides of the comparison came from here.
         */
        const txidHex = inp.txid ? bytesToHex(inp.txid) : '?';
        const outpoint = `${txidHex}:${inp.index ?? 0}`;
        if (seenOutpoints.has(outpoint))
            duplicateOutpoints.add(outpoint);
        seenOutpoints.add(outpoint);
        for (const [, origin] of inp.bip32Derivation ?? []) {
            const els = (origin.path ?? []);
            if (els.length >= 2 && PURPOSES.has((els[0] & ~HARDENED) >>> 0))
                coinTypes.add((els[1] & ~HARDENED) >>> 0);
        }
        const path = utxo ? isMine(ctx.descriptor, utxo.script, ctx.window) : null;
        /*
         * What this input actually spends, so the vsize estimate is not charged the vault's m-of-n
         * witness for a foreign single-sig input. `path !== null` means the scan recognised it as
         * ours, which is the strongest statement available; otherwise the scriptPubKey shape is.
         */
        inputKinds.push(path !== null ? 'wsh-multi' : inputKind(utxo?.script ?? null));
        inputs.push({
            index: i,
            outpoint,
            txid: txidHex,
            vout: inp.index ?? 0,
            amountSats: amount,
            amountBound,
            mine: utxo ? path !== null : null,
            path,
            hasDerivation: !!(inp.bip32Derivation && inp.bip32Derivation.length > 0),
            sighash: inp.sighashType ?? btc.SigHash.ALL,
            sequence: inp.sequence ?? btc.DEFAULT_SEQUENCE,
        });
    }
    const outputs = [];
    const outScriptLengths = [];
    let totalOut = 0n;
    for (let i = 0; i < tx.outputsLength; i++) {
        const out = tx.getOutput(i);
        if (out.script === undefined || out.amount === undefined)
            throw new IntentError('MALFORMED_PSBT', `output ${i} has no script or amount`);
        totalOut += out.amount;
        const { type, standard } = outputType(out.script);
        outScriptLengths.push(out.script.length);
        const path = isMine(ctx.descriptor, out.script, ctx.window);
        outputs.push({
            index: i,
            amountSats: out.amount,
            script: bytesToHex(out.script),
            address: encodeAddress(out.script, ctx.network),
            mine: path !== null,
            path,
            role: path !== null ? 'change' : 'external',
            standard,
            scriptType: type,
        });
    }
    const vsize = estimateVsize(ctx.descriptor, inputKinds, outScriptLengths);
    const feeSats = amountsKnown ? totalIn - totalOut : null;
    return {
        version: tx.version,
        network: ctx.network,
        locktime: tx.lockTime,
        inputs,
        outputs,
        totalInSats: amountsKnown ? totalIn : null,
        totalOutSats: totalOut,
        feeSats,
        vsize,
        // A fee RATE is a small number by nature, so a double is the right type for it — but it is
        // computed from the bigint, never from a lossy copy of it.
        feeRate: feeSats === null ? null : Math.round((Number(feeSats) / vsize) * 10) / 10,
        rbf: inputs.some((i) => i.sequence < 0xfffffffe),
        duplicateOutpoints: [...duplicateOutpoints],
        coinTypes: [...coinTypes].sort((a, b) => a - b),
    };
}
//# sourceMappingURL=intent.js.map